import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./chat_support.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";

interface Ticket {
  _id: string;
  userQuestion: string;
  botQuestion: string;
  userReply: string;
  botFollowUpQuestion: string;
  userFollowUpReply: string;
  botClosingReply: string;
  createdAt: string;
  isTicketOpen: boolean;
  __v: number;
}

interface UserQueryFitbot {
  _id: string;
  userName: string;
  phoneNumber: string;
  tickets: Ticket[];
}

const ChatSupport = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [userQueries, setUserQueries] = useState<UserQueryFitbot[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [employeeToDelete, setEmployeeToDelete] = useState<{
    phoneNumber: string;
    ticketId: string;
  } | null>(null);
  const [showResolved, setShowResolved] = useState<boolean>(false); // Toggle between Open and Resolved
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Formatting date to IST
  function formatDateToIST(dateString: string | number | Date) {
    const date = new Date(dateString);
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Intl.DateTimeFormat("en-IN").format(istDate);
  }

  // Fetch user details
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUserQueries(token);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  // Fetch all user queries (tickets)
  const fetchAllUserQueries = async (token: string) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/fetch-all-users-query-fitbot`,
        config
      );
      setUserQueries(response.data.data); // Array of UserQueryFitbot
    } catch (error) {
      console.error("Failed to fetch users' tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Handle closing a ticket
  const handleQueryResolved = async () => {
    if (!employeeToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(
        `https://api.fitoja.in/api/set-open-ticket-false/${employeeToDelete.phoneNumber}/${employeeToDelete.ticketId}`,
        {},
        config
      );
      fetchAllUserQueries(token);
      closeQueryResolvedModal();
    } catch (error) {
      console.error("Failed to close the ticket:", error);
    }
  };

  const openQueryResolvedModal = (phoneNumber: string, ticketId: string) => {
    setEmployeeToDelete({ phoneNumber, ticketId });
    setDeleteModalVisible(true);
  };

  const closeQueryResolvedModal = () => {
    setDeleteModalVisible(false);
    setEmployeeToDelete(null);
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  // Filter userQueries based on the `isTicketOpen` state (showResolved state)
  const filteredUserQueries = userQueries.map((userQuery) => ({
    ...userQuery,
    tickets: userQuery.tickets
      .filter((ticket) => ticket.isTicketOpen === !showResolved)
      .filter((employee) => userQuery.phoneNumber.includes(searchTerm)),
  }));

  return (
    <div className="chat-support-container">
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <div className="employee-list">
            <h2>All Queries</h2>
            <input
              className="search-box"
              type="text"
              placeholder="Search user by Phone Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="toggle-container">
              <div
                className={`toggle-option ${!showResolved ? "active" : ""}`}
                onClick={() => setShowResolved(false)}
              >
                Open Tickets
              </div>
              <div
                className={`toggle-option ${showResolved ? "active" : ""}`}
                onClick={() => setShowResolved(true)}
              >
                Closed Tickets
              </div>
            </div>

            {filteredUserQueries.some(
              (userQuery) => userQuery.tickets.length > 0
            ) ? (
              filteredUserQueries.map((userQuery) =>
                userQuery.tickets.map((ticket) => (
                  <div key={ticket._id} className="ticket-sub-container">
                    <div className="sub-ticket-card">
                      <p>
                        <strong>Name:</strong> {userQuery.userName}
                      </p>
                      <p>
                        <strong>Phone Number:</strong> {userQuery.phoneNumber}
                      </p>
                      <p>
                        <strong>Query:</strong> {ticket.userQuestion}
                      </p>
                      <p>
                        <strong>Bot Response:</strong> {ticket.botQuestion}
                      </p>
                      <p>
                        <strong>User Response:</strong> {ticket.userReply}
                      </p>
                      {ticket.botFollowUpQuestion && (
                        <p>
                          <strong>Bot Followup question:</strong>{" "}
                          {ticket.botFollowUpQuestion}
                        </p>
                      )}
                      {ticket.userFollowUpReply && (
                        <p>
                          <strong>User Followup reply:</strong>{" "}
                          {ticket.userFollowUpReply}
                        </p>
                      )}
                      <p>
                        <strong>Closing Response:</strong>{" "}
                        {ticket.botClosingReply}
                      </p>
                      {ticket.isTicketOpen && (
                        <button
                          className="close-ticket-button"
                          onClick={() =>
                            openQueryResolvedModal(
                              userQuery.phoneNumber,
                              ticket._id
                            )
                          }
                        >
                          Mark as Resolved
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )
            ) : (
              <div>No tickets to show</div>
            )}
          </div>

          {deleteModalVisible && (
            <div className="confirmation-modal">
              <div className="modal-content">
                <h3>Are you sure you want to mark this ticket as resolved?</h3>
                <div className="modal-actions">
                  <button onClick={handleQueryResolved}>Yes</button>
                  <button onClick={closeQueryResolvedModal}>No</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatSupport;
